import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  memo,
  useRef,
} from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  Typography,
  Input,
  Textarea,
  Select,
  Option,
  Checkbox,
  Stack,
  IconButton,
  Sheet,
  FormControl,
  FormLabel,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Divider,
  Chip,
  Alert,
  Breadcrumbs,
  Link,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
} from "@mui/joy";
import {
  ArrowLeft as BackIcon,
  FloppyDisk as SaveIcon,
  Eye as PreviewIcon,
  Trash as DeleteIcon,
  DotsSixVertical as DragIcon,
  Copy as CopyIcon,
  Code as CodeIcon,
  CheckCircle as ActiveIcon,
  Circle as InactiveIcon,
  Plus as AddIcon,
} from "@phosphor-icons/react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { FormEmbed } from "./FormEmbed";
import { publishingIssues } from "../../lib/formTemplates";
import { formsApi, type Form, type FormField } from "../../lib/apiMarketing";
import { useContactSources } from "../../hooks/useCrm";

// `crm_field` binds an answer to a CRM column. It rides inside the
// form_schema JSON — FormField has no column for it — so it isn't on the
// shared API type.
type BuilderField = FormField & { crm_field?: string };

// Must stay in sync with CONTACT_CRM_FIELDS / ADDRESS_CRM_FIELDS in
// backend/apexos/apps/marketing/serializers_forms.py.
const CRM_FIELD_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Don't map to CRM" },
  { value: "contact.first_name", label: "Contact · First Name" },
  { value: "contact.last_name", label: "Contact · Last Name" },
  { value: "contact.full_name", label: "Contact · Full Name (auto-split)" },
  { value: "contact.email", label: "Contact · Email" },
  { value: "contact.phone", label: "Contact · Phone" },
  { value: "contact.secondary_phone", label: "Contact · Secondary Phone" },
  { value: "contact.company_name", label: "Contact · Company" },
  { value: "contact.title", label: "Contact · Title" },
  { value: "contact.website", label: "Contact · Website" },
  {
    value: "address.full_address",
    label: "Address · Full Address (auto-split)",
  },
  { value: "address.street_address_1", label: "Address · Street" },
  { value: "address.street_address_2", label: "Address · Street 2" },
  { value: "address.city", label: "Address · City" },
  { value: "address.state_province", label: "Address · State" },
  { value: "address.postal_code", label: "Address · Zip" },
];

// Field type definitions
// Individual field input with local state to prevent focus loss
interface FieldInputProps {
  label: string;
  value: string | undefined;
  fieldId: string;
  fieldKey: string;
  onUpdate: (fieldId: string, updates: Partial<BuilderField>) => void;
  placeholder?: string;
}

const FieldInput: React.FC<FieldInputProps> = ({
  label,
  value,
  fieldId,
  fieldKey,
  onUpdate,
  placeholder,
}) => {
  const [localValue, setLocalValue] = useState(value || "");

  useEffect(() => {
    setLocalValue(value || "");
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onUpdate(fieldId, { [fieldKey]: newValue });
  };

  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <Input
        value={localValue}
        onChange={handleChange}
        placeholder={placeholder}
      />
    </FormControl>
  );
};

// Checkbox with local state
interface FieldCheckboxProps {
  label: string;
  checked: boolean;
  fieldId: string;
  fieldKey: string;
  onUpdate: (fieldId: string, updates: Partial<BuilderField>) => void;
}

const FieldCheckbox: React.FC<FieldCheckboxProps> = ({
  label,
  checked,
  fieldId,
  fieldKey,
  onUpdate,
}) => {
  const [localChecked, setLocalChecked] = useState(checked);

  useEffect(() => {
    setLocalChecked(checked);
  }, [checked]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.checked;
    setLocalChecked(newValue);
    // Update immediately for checkboxes since there's no blur event
    onUpdate(fieldId, { [fieldKey]: newValue });
  };

  return (
    <FormControl>
      <Checkbox label={label} checked={localChecked} onChange={handleChange} />
    </FormControl>
  );
};

// Select with local state
interface FieldSelectProps {
  label: string;
  value: string;
  fieldId: string;
  fieldKey: string;
  onUpdate: (fieldId: string, updates: Partial<BuilderField>) => void;
  options: { value: string; label: string }[];
}

const FieldSelect: React.FC<FieldSelectProps> = ({
  label,
  value,
  fieldId,
  fieldKey,
  onUpdate,
  options,
}) => {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (_: any, newValue: string | null) => {
    if (newValue) {
      setLocalValue(newValue);
      // Update immediately for selects
      onUpdate(fieldId, { [fieldKey]: newValue });
    }
  };

  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <Select value={localValue} onChange={handleChange}>
        {options.map((opt) => (
          <Option key={opt.value} value={opt.value}>
            {opt.label}
          </Option>
        ))}
      </Select>
    </FormControl>
  );
};

// Binds one answer to a CRM column. Kept separate from FieldSelect because
// that one ignores falsy values, so a mapping could never be removed once set.
interface CrmFieldSelectProps {
  value: string | undefined;
  fieldId: string;
  onUpdate: (fieldId: string, updates: Partial<BuilderField>) => void;
}

const CrmFieldSelect: React.FC<CrmFieldSelectProps> = ({
  value,
  fieldId,
  onUpdate,
}) => {
  const [localValue, setLocalValue] = useState(value || "");

  useEffect(() => {
    setLocalValue(value || "");
  }, [value, fieldId]);

  const handleChange = (_: unknown, newValue: string | null) => {
    const next = newValue ?? "";
    setLocalValue(next);
    onUpdate(fieldId, { crm_field: next });
  };

  return (
    <FormControl>
      <FormLabel>Maps to CRM field</FormLabel>
      <Select value={localValue} onChange={handleChange}>
        {CRM_FIELD_OPTIONS.map((opt) => (
          <Option key={opt.value || "none"} value={opt.value}>
            {opt.label}
          </Option>
        ))}
      </Select>
      <Typography level="body-xs" textColor="text.tertiary" sx={{ mt: 0.5 }}>
        Unmapped answers are still stored on the submission — they just don't
        populate the contact or its address.
      </Typography>
    </FormControl>
  );
};

// Options editor for dropdown / radio fields
interface DropdownOptionsEditorProps {
  fieldId: string;
  initialOptions: Array<{ label: string; value: string }>;
  onUpdate: (fieldId: string, updates: Partial<BuilderField>) => void;
}

const DropdownOptionsEditor: React.FC<DropdownOptionsEditorProps> = ({
  fieldId,
  initialOptions,
  onUpdate,
}) => {
  const [options, setOptions] =
    useState<Array<{ label: string; value: string }>>(initialOptions);
  // Track whether the value was manually edited so we stop auto-deriving it
  const manualValueRef = useRef<Record<number, boolean>>({});

  useEffect(() => {
    setOptions(initialOptions);
    manualValueRef.current = {};
  }, [fieldId]); // eslint-disable-line react-hooks/exhaustive-deps

  const slugify = (text: string) =>
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "");

  const handleLabelChange = (index: number, label: string) => {
    setOptions((prev) => {
      const updated = prev.map((opt, i) => {
        if (i !== index) return opt;
        const value = manualValueRef.current[index]
          ? opt.value
          : slugify(label);
        return { label, value };
      });
      onUpdate(fieldId, { options: updated });
      return updated;
    });
  };

  const handleValueChange = (index: number, value: string) => {
    manualValueRef.current[index] = true;
    setOptions((prev) => {
      const updated = prev.map((opt, i) =>
        i === index ? { ...opt, value } : opt,
      );
      onUpdate(fieldId, { options: updated });
      return updated;
    });
  };

  const handleAdd = () => {
    setOptions((prev) => {
      const updated = [...prev, { label: "", value: "" }];
      onUpdate(fieldId, { options: updated });
      return updated;
    });
  };

  const handleRemove = (index: number) => {
    manualValueRef.current = Object.fromEntries(
      Object.entries(manualValueRef.current)
        .filter(([k]) => Number(k) !== index)
        .map(([k, v]) => [Number(k) > index ? Number(k) - 1 : Number(k), v]),
    );
    setOptions((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      onUpdate(fieldId, { options: updated });
      return updated;
    });
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1 }}
      >
        <Typography level="title-sm">Options</Typography>
        <Button
          size="sm"
          variant="soft"
          startDecorator={<AddIcon />}
          onClick={handleAdd}
        >
          Add Option
        </Button>
      </Stack>
      {options.length === 0 && (
        <Typography level="body-xs" textColor="text.secondary" sx={{ mb: 1 }}>
          No options yet. Click "Add Option" to add choices.
        </Typography>
      )}
      <Stack spacing={1}>
        {options.map((opt, i) => (
          <Stack key={i} direction="row" spacing={1} alignItems="center">
            <Input
              size="sm"
              placeholder="Label"
              value={opt.label}
              onChange={(e) => handleLabelChange(i, e.target.value)}
              sx={{ flex: 1 }}
            />
            <Input
              size="sm"
              placeholder="Value"
              value={opt.value}
              onChange={(e) => handleValueChange(i, e.target.value)}
              sx={{ flex: 1 }}
            />
            <IconButton
              size="sm"
              color="danger"
              onClick={() => handleRemove(i)}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

// Memoized component for field settings to prevent focus loss
interface FieldSettingsProps {
  fieldId: string;
  fields: BuilderField[];
  onUpdate: (fieldId: string, updates: Partial<BuilderField>) => void;
}

const FieldSettings = memo<FieldSettingsProps>(
  ({ fieldId, fields, onUpdate }) => {
    // Find the field inside the component so the fieldId prop stays stable
    const field = fields.find((f) => f.id === fieldId);

    if (!field) {
      return (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography level="body-md" textColor="text.secondary">
            Field not found
          </Typography>
        </Box>
      );
    }

    return (
      <Stack spacing={2}>
        <Typography level="title-md">Field Settings</Typography>

        <FieldInput
          label="Label"
          value={field.label}
          fieldId={fieldId}
          fieldKey="label"
          onUpdate={onUpdate}
        />

        <FieldInput
          label="Field Name"
          value={field.name}
          fieldId={fieldId}
          fieldKey="name"
          onUpdate={onUpdate}
        />

        {!["heading", "paragraph", "divider"].includes(field.field_type) && (
          <CrmFieldSelect
            value={field.crm_field}
            fieldId={fieldId}
            onUpdate={onUpdate}
          />
        )}

        {["text", "email", "phone", "textarea", "select"].includes(
          field.field_type,
        ) && (
          <FieldInput
            label="Placeholder"
            value={field.placeholder}
            fieldId={fieldId}
            fieldKey="placeholder"
            onUpdate={onUpdate}
          />
        )}

        <FieldInput
          label="Help Text"
          value={field.help_text}
          fieldId={fieldId}
          fieldKey="help_text"
          onUpdate={onUpdate}
        />

        <FieldCheckbox
          label="Required"
          checked={field.required}
          fieldId={fieldId}
          fieldKey="required"
          onUpdate={onUpdate}
        />

        <FieldSelect
          label="Width"
          value={field.width}
          fieldId={fieldId}
          fieldKey="width"
          onUpdate={onUpdate}
          options={[
            { value: "full", label: "Full Width" },
            { value: "half", label: "Half Width" },
            { value: "third", label: "One Third" },
            { value: "two_thirds", label: "Two Thirds" },
          ]}
        />

        {["select", "radio"].includes(field.field_type) && (
          <DropdownOptionsEditor
            fieldId={fieldId}
            initialOptions={field.options || []}
            onUpdate={onUpdate}
          />
        )}
      </Stack>
    );
  },
  // Custom comparison function - only re-render if fieldId changes
  (prevProps, nextProps) => {
    return (
      prevProps.fieldId === nextProps.fieldId &&
      prevProps.onUpdate === nextProps.onUpdate
    );
  },
);

FieldSettings.displayName = "FieldSettings";

const FIELD_TYPES = [
  { value: "text", label: "Text Input", icon: "📝" },
  { value: "email", label: "Email", icon: "📧" },
  { value: "phone", label: "Phone", icon: "📱" },
  { value: "number", label: "Number", icon: "🔢" },
  { value: "textarea", label: "Text Area", icon: "📄" },
  { value: "select", label: "Dropdown", icon: "📋" },
  { value: "radio", label: "Radio Buttons", icon: "⭕" },
  { value: "checkbox", label: "Checkbox", icon: "☑️" },
  { value: "date", label: "Date", icon: "📅" },
  { value: "heading", label: "Section Heading", icon: "📌" },
  { value: "paragraph", label: "Paragraph Text", icon: "📝" },
  { value: "divider", label: "Divider", icon: "➖" },
];

// Sortable field card wrapper for drag-and-drop reordering
interface SortableFieldCardProps {
  field: BuilderField;
  selectedFieldId: string | null;
  onSelect: (id: string) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
}

const SortableFieldCard: React.FC<SortableFieldCardProps> = ({
  field,
  selectedFieldId,
  onSelect,
  onDuplicate,
  onDelete,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: field.id!,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: "default",
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card
        variant={selectedFieldId === field.id ? "outlined" : "soft"}
        sx={{
          cursor: "pointer",
          border: selectedFieldId === field.id ? "2px solid" : undefined,
          borderColor: "primary.500",
        }}
        onClick={() => onSelect(field.id!)}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton
            size="sm"
            sx={{ cursor: "grab", "&:active": { cursor: "grabbing" } }}
            {...attributes}
            {...listeners}
          >
            <DragIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography level="title-sm">{field.label}</Typography>
            <Typography level="body-xs" textColor="text.secondary">
              {field.field_type} {field.required && "(required)"}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5}>
            <IconButton
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate(field.id!);
              }}
            >
              <CopyIcon />
            </IconButton>
            <IconButton
              size="sm"
              color="danger"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(field.id!);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        </Stack>

        {/* Field Preview */}
        <Box sx={{ mt: 2 }}>
          {field.field_type === "text" && (
            <Input placeholder={field.placeholder} />
          )}
          {field.field_type === "email" && (
            <Input type="email" placeholder={field.placeholder} />
          )}
          {field.field_type === "phone" && (
            <Input type="tel" placeholder={field.placeholder} />
          )}
          {field.field_type === "textarea" && (
            <Textarea placeholder={field.placeholder} minRows={3} />
          )}
          {field.field_type === "select" && (
            <Select placeholder={field.placeholder}>
              {field.options?.map((opt, i) => (
                <Option key={i} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          )}
          {field.field_type === "checkbox" && <Checkbox label={field.label} />}
          {field.field_type === "heading" && (
            <Typography level="h4">{field.label}</Typography>
          )}
          {field.field_type === "paragraph" && (
            <Typography>{field.label}</Typography>
          )}
          {field.field_type === "divider" && <Divider />}
        </Box>
      </Card>
    </div>
  );
};

export const FormBuilder: React.FC = () => {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<Form>>({});
  const [fields, setFields] = useState<BuilderField[]>([]);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [embedCode, setEmbedCode] = useState<{
    iframe: string;
    script: string;
    direct_url: string;
  } | null>(null);

  // Sort fields once using useMemo
  const sortedFields = useMemo(() => {
    return [...fields].sort((a, b) => a.order - b.order);
  }, [fields]);

  // Get the selected field by ID
  const selectedField = useMemo(() => {
    return fields.find((f) => f.id === selectedFieldId) || null;
  }, [fields, selectedFieldId]);

  // Fetch form data
  const { data: form, isLoading } = useQuery({
    queryKey: ["form", formId],
    queryFn: () => formsApi.get(formId!),
    enabled: !!formId,
  });

  const { data: contactSources } = useContactSources();

  // Update form mutation — no invalidateQueries to avoid overwriting local state mid-save
  const updateFormMutation = useMutation({
    mutationFn: (data: Partial<Form>) => formsApi.update(formId!, data),
    onError: () => {
      toast.error("Failed to save form");
    },
  });

  const initializedForm = useRef<string | null>(null);
  const [previewSize, setPreviewSize] = useState<"desktop" | "mobile" | null>(
    null,
  );

  // Initialize form data
  useEffect(() => {
    if (form?.data && initializedForm.current !== formId) {
      initializedForm.current = formId!;
      setFormData(form.data);
      // Extract fields from schema if they exist
      if (form.data.form_schema?.fields) {
        // Ensure all fields have IDs for stable React keys
        const fieldsWithIds = form.data.form_schema.fields.map(
          (field: BuilderField, index: number) => ({
            ...field,
            id:
              field.id ||
              `field_${index}_${Math.random().toString(36).substr(2, 9)}`,
          }),
        );
        setFields(fieldsWithIds);
      }
    }
  }, [form]);

  const handleSave = async () => {
    try {
      await updateFormMutation.mutateAsync({
        ...formData,
        form_schema: { ...formData.form_schema, fields },
      });
      toast.success("Form saved successfully");
    } catch {
      // Individual mutation error handlers already show toasts
    }
  };

  const handleGetEmbedCode = async () => {
    if (!formId) return;

    try {
      const response = await formsApi.getEmbedCode(formId);
      setEmbedCode(response.data);
      setShowEmbedModal(true);
    } catch (error) {
      toast.error("Failed to get embed code");
    }
  };

  const handleCopyToClipboard = (text: string, type: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success(`${type} copied to clipboard!`);
      })
      .catch(() => {
        toast.error("Failed to copy to clipboard");
      });
  };

  const handleAddField = useCallback((fieldType: string) => {
    const fieldId = `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setFields((prev) => {
      const newField: BuilderField = {
        id: fieldId,
        field_type: fieldType as any,
        name: `field_${Date.now()}`,
        label: `New ${fieldType} field`,
        placeholder: "",
        help_text: "",
        required: false,
        validation_rules: {},
        options: [],
        order: prev.length,
        width: "full",
        conditional_logic: {},
      };
      return [...prev, newField];
    });
    setSelectedFieldId(fieldId);
  }, []);

  const handleUpdateField = useCallback(
    (fieldId: string, updates: Partial<BuilderField>) => {
      setFields((prev) =>
        prev.map((field) =>
          field.id === fieldId ? { ...field, ...updates } : field,
        ),
      );
    },
    [],
  );

  const handleDeleteField = useCallback((fieldId: string) => {
    setFields((prev) => {
      const updatedFields = prev.filter((f) => f.id !== fieldId);
      // Reorder remaining fields
      updatedFields.forEach((field, i) => {
        field.order = i;
      });
      return updatedFields;
    });

    // Clear selection if deleting selected field
    setSelectedFieldId((prev) => (prev === fieldId ? null : prev));
  }, []);

  const handleDuplicateField = useCallback((fieldId: string) => {
    const newFieldId = `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    setFields((prev) => {
      const fieldToCopy = prev.find((f) => f.id === fieldId);
      if (!fieldToCopy) return prev;

      const newField = {
        ...fieldToCopy,
        id: newFieldId,
        name: `${fieldToCopy.name}_copy`,
        label: `${fieldToCopy.label} (Copy)`,
        order: prev.length,
      };

      return [...prev, newField];
    });

    setSelectedFieldId(newFieldId);
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setFields((prev) => {
      const oldIndex = prev.findIndex((f) => f.id === active.id);
      const newIndex = prev.findIndex((f) => f.id === over.id);
      const reordered = arrayMove(prev, oldIndex, newIndex);
      return reordered.map((f, i) => ({ ...f, order: i }));
    });
  }, []);

  const sensors = useSensors(useSensor(PointerSensor));

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Typography>Loading form...</Typography>
      </Box>
    );
  }

  if (!form) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="danger">Form not found</Alert>
        <Button onClick={() => navigate("/marketing/forms")} sx={{ mt: 2 }}>
          Back to Forms
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Sheet
        variant="soft"
        sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton onClick={() => navigate("/marketing/forms")}>
              <BackIcon />
            </IconButton>
            <Box>
              <Typography level="h4">
                {formData.name || "Untitled Form"}
              </Typography>
              <Breadcrumbs size="sm">
                <Link onClick={() => navigate("/marketing")}>Marketing</Link>
                <Link onClick={() => navigate("/marketing/forms")}>Forms</Link>
                <Typography>Edit</Typography>
              </Breadcrumbs>
            </Box>
            <Chip
              size="sm"
              color={formData.status === "active" ? "success" : "neutral"}
              startDecorator={
                formData.status === "active" ? <ActiveIcon /> : <InactiveIcon />
              }
            >
              {formData.status || "draft"}
            </Chip>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              color="primary"
              startDecorator={<PreviewIcon />}
              onClick={() => setPreviewSize("desktop")}
              disabled={!form?.data?.embed_id}
            >
              Preview
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startDecorator={<CodeIcon />}
              onClick={handleGetEmbedCode}
            >
              Get Embed Code
            </Button>
            <Button
              startDecorator={<SaveIcon />}
              onClick={handleSave}
              loading={updateFormMutation.isPending}
            >
              Save Changes
            </Button>
          </Stack>
        </Stack>
      </Sheet>

      {previewSize && (
        <Modal open onClose={() => setPreviewSize(null)}>
          <ModalDialog
            sx={{
              width: previewSize === "mobile" ? 390 : 900,
              maxWidth: "95vw",
              overflow: "auto",
            }}
          >
            <ModalClose />
            <Typography level="h4">Preview & publishing checklist</Typography>
            <Button
              variant="outlined"
              onClick={() =>
                setPreviewSize(previewSize === "mobile" ? "desktop" : "mobile")
              }
            >
              Switch to {previewSize === "mobile" ? "desktop" : "mobile"}
            </Button>
            {publishingIssues({ ...formData, form_schema: { fields } }).map(
              (issue) => (
                <Typography key={issue} color="warning">
                  {issue}
                </Typography>
              ),
            )}
            <FormEmbed
              previewForm={{ ...formData, form_schema: { fields } } as Form}
            />
          </ModalDialog>
        </Modal>
      )}
      {/* Main Content */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Left Sidebar - Field Types */}
        <Sheet
          sx={{
            width: 250,
            p: 2,
            borderRight: "1px solid",
            borderColor: "divider",
            overflowY: "auto",
          }}
        >
          <Typography level="title-md" sx={{ mb: 2 }}>
            Add Fields
          </Typography>
          <Stack spacing={1}>
            {FIELD_TYPES.map((type) => (
              <Button
                key={type.value}
                variant="soft"
                size="sm"
                startDecorator={<span>{type.icon}</span>}
                onClick={() => handleAddField(type.value)}
                sx={{ justifyContent: "flex-start" }}
              >
                {type.label}
              </Button>
            ))}
          </Stack>
        </Sheet>

        {/* Center - Form Builder */}
        <Box
          sx={{
            flex: 1,
            p: 3,
            overflowY: "auto",
            bgcolor: "background.level1",
          }}
        >
          <Card sx={{ maxWidth: 800, mx: "auto" }}>
            <Typography level="h3" sx={{ mb: 2 }}>
              {formData.name}
            </Typography>
            {formData.description && (
              <Typography level="body-md" sx={{ mb: 3 }}>
                {formData.description}
              </Typography>
            )}

            <Stack spacing={2}>
              {fields.length === 0 ? (
                <Box
                  sx={{
                    py: 4,
                    textAlign: "center",
                    border: "2px dashed",
                    borderColor: "neutral.300",
                    borderRadius: "sm",
                  }}
                >
                  <Typography level="body-lg" sx={{ mb: 1 }}>
                    No fields yet
                  </Typography>
                  <Typography level="body-sm" textColor="text.secondary">
                    Add fields from the left sidebar to start building your form
                  </Typography>
                </Box>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={sortedFields.map((f) => f.id!)}
                    strategy={verticalListSortingStrategy}
                  >
                    {sortedFields.map((field) => (
                      <SortableFieldCard
                        key={field.id}
                        field={field}
                        selectedFieldId={selectedFieldId}
                        onSelect={(id) => setSelectedFieldId(id)}
                        onDuplicate={handleDuplicateField}
                        onDelete={handleDeleteField}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )}

              {/* Submit Button Preview */}
              <Button size="lg" sx={{ mt: 2 }}>
                {formData.submit_button_text || "Submit"}
              </Button>
            </Stack>
          </Card>
        </Box>

        {/* Right Sidebar - Field Settings */}
        <Sheet
          sx={{ width: 350, borderLeft: "1px solid", borderColor: "divider" }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value as number)}
          >
            <TabList>
              <Tab>Field Settings</Tab>
              <Tab>Form Settings</Tab>
            </TabList>

            <TabPanel value={0} sx={{ p: 2 }}>
              {selectedFieldId ? (
                <FieldSettings
                  fieldId={selectedFieldId}
                  fields={fields}
                  onUpdate={handleUpdateField}
                />
              ) : (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <Typography level="body-md" textColor="text.secondary">
                    Select a field to edit its settings
                  </Typography>
                </Box>
              )}
            </TabPanel>

            <TabPanel
              value={1}
              sx={{ p: 2, overflowY: "auto", maxHeight: "calc(100vh - 200px)" }}
            >
              <Stack spacing={2}>
                <Typography level="title-md">Form Settings</Typography>

                <FormControl>
                  <FormLabel>Form Name</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    minRows={2}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Submit Button Text</FormLabel>
                  <Input
                    value={formData.submit_button_text}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        submit_button_text: e.target.value,
                      })
                    }
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Success Message</FormLabel>
                  <Textarea
                    value={formData.success_message}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        success_message: e.target.value,
                      })
                    }
                    minRows={2}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>After Submission</FormLabel>
                  <Select
                    value={formData.submit_action}
                    onChange={(_, value) =>
                      setFormData({ ...formData, submit_action: value as any })
                    }
                  >
                    <Option value="message">Show Message</Option>
                    <Option value="redirect">Redirect to URL</Option>
                    <Option value="download">Download File</Option>
                  </Select>
                </FormControl>

                {formData.submit_action === "redirect" && (
                  <FormControl>
                    <FormLabel>Redirect URL</FormLabel>
                    <Input
                      value={formData.submit_action_value}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          submit_action_value: e.target.value,
                        })
                      }
                      placeholder="https://example.com/thank-you"
                    />
                  </FormControl>
                )}

                <Divider />

                <Typography level="title-sm">CRM</Typography>

                <FormControl>
                  <Checkbox
                    label="Create a contact from each submission"
                    checked={formData.create_contact ?? true}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        create_contact: e.target.checked,
                      })
                    }
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Lead source</FormLabel>
                  <Select
                    // The API returns the FK as a number; the options are
                    // keyed by string so they'd never match otherwise.
                    value={
                      formData.contact_source != null
                        ? String(formData.contact_source)
                        : ""
                    }
                    onChange={(_, value) =>
                      setFormData({
                        // null rather than undefined so clearing the source
                        // survives JSON.stringify and reaches the API.
                        ...formData,
                        contact_source: (value ||
                          null) as unknown as Form["contact_source"],
                      })
                    }
                  >
                    <Option value="">Website Form (default)</Option>
                    {(contactSources || []).map((source) => (
                      <Option key={source.id} value={String(source.id)}>
                        {source.name}
                      </Option>
                    ))}
                  </Select>
                  <Typography
                    level="body-xs"
                    sx={{ mt: 0.5, color: "text.tertiary" }}
                  >
                    Stamped on every contact this form creates, so web leads
                    show up in Lead Source ROI.
                  </Typography>
                </FormControl>

                <Divider />

                <Typography level="title-sm">Email Notifications</Typography>

                <FormControl>
                  <Checkbox
                    label="Send email notifications"
                    checked={formData.email_notification_enabled}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        email_notification_enabled: e.target.checked,
                      })
                    }
                  />
                </FormControl>

                {formData.email_notification_enabled && (
                  <>
                    <FormControl>
                      <FormLabel>Email Subject</FormLabel>
                      <Input
                        value={formData.email_notification_subject}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            email_notification_subject: e.target.value,
                          })
                        }
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>Send To (comma separated)</FormLabel>
                      <Input
                        value={formData.email_notification_to?.join(", ")}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            email_notification_to: e.target.value
                              .split(",")
                              .map((email) => email.trim())
                              .filter(Boolean),
                          })
                        }
                        placeholder="admin@example.com, sales@example.com"
                      />
                    </FormControl>
                  </>
                )}

                <Divider />

                <Typography level="title-sm">Tracking</Typography>

                <FormControl>
                  <FormLabel>Facebook Pixel ID</FormLabel>
                  <Input
                    value={formData.facebook_pixel_id}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        facebook_pixel_id: e.target.value,
                      })
                    }
                    placeholder="Optional"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Google Analytics ID</FormLabel>
                  <Input
                    value={formData.google_analytics_id}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        google_analytics_id: e.target.value,
                      })
                    }
                    placeholder="Optional"
                  />
                </FormControl>

                <Divider />

                <Typography level="title-sm">Embed Security</Typography>

                <FormControl>
                  <FormLabel>Allowed Embed Domains</FormLabel>
                  <Textarea
                    value={(formData.allowed_embed_domains || []).join(", ")}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        allowed_embed_domains: e.target.value
                          .split(",")
                          .map((domain) => domain.trim().toLowerCase())
                          .filter(Boolean),
                      })
                    }
                    placeholder="example.com, blog.example.com"
                    minRows={2}
                  />
                  <Typography
                    level="body-xs"
                    sx={{ mt: 0.5, color: "text.tertiary" }}
                  >
                    Enter domains separated by commas. Leave empty to allow
                    embedding on any site. Submissions from non-whitelisted
                    domains will be flagged for review.
                  </Typography>
                </FormControl>
              </Stack>
            </TabPanel>
          </Tabs>
        </Sheet>
      </Box>

      {/* Embed Code Modal */}
      <Modal open={showEmbedModal} onClose={() => setShowEmbedModal(false)}>
        <ModalDialog size="lg" sx={{ maxWidth: 800 }}>
          <ModalClose />
          <DialogTitle>Embed Form</DialogTitle>
          <DialogContent>
            <Typography level="body-md" sx={{ mb: 3 }}>
              Choose how you want to embed "{formData.name}" on your website:
            </Typography>

            <Stack spacing={3}>
              {/* Direct Link */}
              <Box>
                <Typography level="title-md" sx={{ mb: 1 }}>
                  Direct Link
                </Typography>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  Share this link directly with your users
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Input
                    value={embedCode?.direct_url || ""}
                    readOnly
                    sx={{ flex: 1 }}
                    endDecorator={
                      <IconButton
                        size="sm"
                        onClick={() =>
                          handleCopyToClipboard(
                            embedCode?.direct_url || "",
                            "Direct link",
                          )
                        }
                      >
                        <CopyIcon />
                      </IconButton>
                    }
                  />
                </Stack>
              </Box>

              {/* IFrame Embed */}
              <Box>
                <Typography level="title-md" sx={{ mb: 1 }}>
                  IFrame Embed (Recommended)
                </Typography>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  Add this code to your website HTML where you want the form to
                  appear
                </Typography>
                <Textarea
                  value={embedCode?.iframe || ""}
                  readOnly
                  minRows={3}
                  sx={{ fontFamily: "monospace", fontSize: "sm" }}
                  endDecorator={
                    <Box sx={{ display: "flex", gap: 1, pt: 1 }}>
                      <Button
                        size="sm"
                        variant="soft"
                        startDecorator={<CopyIcon />}
                        onClick={() =>
                          handleCopyToClipboard(
                            embedCode?.iframe || "",
                            "IFrame code",
                          )
                        }
                      >
                        Copy Code
                      </Button>
                    </Box>
                  }
                />
              </Box>

              {/* JavaScript Embed */}
              <Box>
                <Typography level="title-md" sx={{ mb: 1 }}>
                  JavaScript Embed
                </Typography>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  For dynamic embedding with JavaScript
                </Typography>
                <Textarea
                  value={embedCode?.script || ""}
                  readOnly
                  minRows={3}
                  sx={{ fontFamily: "monospace", fontSize: "sm" }}
                  endDecorator={
                    <Box sx={{ display: "flex", gap: 1, pt: 1 }}>
                      <Button
                        size="sm"
                        variant="soft"
                        startDecorator={<CopyIcon />}
                        onClick={() =>
                          handleCopyToClipboard(
                            embedCode?.script || "",
                            "JavaScript code",
                          )
                        }
                      >
                        Copy Code
                      </Button>
                    </Box>
                  }
                />
              </Box>

              {/* Instructions */}
              <Alert color="primary" sx={{ mt: 2 }}>
                <Typography level="body-sm">
                  <strong>Tips:</strong>
                  <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
                    <li>The IFrame method is recommended for most websites</li>
                    <li>
                      Adjust the width and height values in the IFrame code to
                      fit your layout
                    </li>
                    <li>The form will automatically adapt to mobile devices</li>
                    <li>
                      Make sure your form status is "Active" for it to be
                      accessible
                    </li>
                  </ul>
                </Typography>
              </Alert>
            </Stack>
          </DialogContent>
        </ModalDialog>
      </Modal>
    </Box>
  );
};
